import type { InstalacionDeInforme } from "@/lib/informes";
import { textoDeDecimal, textoDeNumero } from "@/lib/montos";
import type {
  CampoDeTerreno,
  ItemDeTerreno,
  RegistroDeTerrenoDelInforme,
  SeccionDeTerreno,
} from "@/lib/terreno";

import type { ModoDelTerreno } from "./cuadroDeConstrucciones";
import { INSTALACION_VACIA, type FormularioDeInstalacion } from "./instalaciones";
import type { OpcionParaVincular } from "./maquinariasTerreno";

export { ausenteEnTerreno, type OpcionParaVincular } from "./maquinariasTerreno";

/* ---------------------------------------------------------------------------
   Lo que la pestaña "Instalaciones y obras complementarias" lee del registro de
   terreno: las instalaciones que el encargado creó en el predio, los registros
   que cuelgan de cada fila y si una instalación de la web "no está en el
   predio".

   Es lógica PURA, gemela de `maquinariasTerreno.ts`, y por lo mismo: las
   instalaciones se guardan de a UNA (POST/PATCH), así que una de terreno se
   ADOPTA abriendo el formulario de alta precargado, y "Es la misma que" es un
   PATCH de la fila elegida.
--------------------------------------------------------------------------- */

const PREFIJO_DE_TERRENO = "instalacion-terreno:";

/** Una instalación que el encargado creó en la App y que ninguna fila adoptó todavía. */
export interface ItemDeInstalacionPorEnlazar {
  uuid: string;
  item: ItemDeTerreno;
}

/** Todo lo que la lista de instalaciones necesita saber del terreno. */
export interface TerrenoDeLaLista {
  informeId: number;
  modo: ModoDelTerreno;
  /** Quien fue a terreno: sus iniciales encabezan el registro. */
  encargado: string | null;
  items: ItemDeTerreno[];
  porEnlazar: ItemDeInstalacionPorEnlazar[];
  onAbrirFoto: (uuid: string) => void;
  /** "Es la misma que": enlaza el registro de terreno a una fila ya guardada. */
  onVincular: (uuid: string, instalacionId: number) => void;
  /** «Quitar de la lista» del aviso de ausencia. */
  onQuitar?: (filaId: number) => void;
  /** «Mantener» del aviso de ausencia. */
  onMantener?: (filaId: number) => void;
}

/**
 * ¿La instalación nació en la App y todavía no tiene datos de valorización?
 * Ni valor de reposición ni valor comercial; con uno solo ya empezó. `null` es
 * una fila TERRENO sin guardar. Misma regla que la de maquinaria.
 */
export function creadaEnTerrenoSinValorizar(
  fila: Pick<InstalacionDeInforme, "terreno_ref" | "valor_reposicion_uf_un" | "valor_comercial_uf_un"> | null,
): boolean {
  if (fila === null) return true;

  return (
    fila.terreno_ref !== null &&
    fila.valor_reposicion_uf_un === null &&
    fila.valor_comercial_uf_un === null
  );
}

/** La sección de instalaciones del registro de terreno, o `null`. */
export function seccionDeInstalaciones(
  registro: RegistroDeTerrenoDelInforme | undefined,
): SeccionDeTerreno | null {
  return registro?.secciones.find((s) => s.clave === "instalaciones") ?? null;
}

/**
 * Las instalaciones de terreno que todavía no tienen fila en el cuadro (el
 * servidor cuelga las ya enlazadas de `instalacion:<id>` y deja de listarlas
 * aparte).
 *
 * TAMBIÉN SE DESCARTA LO QUE UNA FILA GUARDADA YA ENLAZA: entre el alta con
 * `terreno_ref` y la relectura del registro hay un instante en que el informe
 * ya tiene la fila y el registro todavía lista la instalación suelta, y se
 * vería dos veces.
 */
export function itemsPorEnlazarDeInstalaciones(
  registro: RegistroDeTerrenoDelInforme | undefined,
  guardadas: Pick<InstalacionDeInforme, "terreno_ref">[] = [],
): ItemDeInstalacionPorEnlazar[] {
  const enlazadas = new Set(guardadas.flatMap((i) => (i.terreno_ref ? [i.terreno_ref] : [])));

  return (seccionDeInstalaciones(registro)?.items ?? []).flatMap((item) => {
    if (!item.nuevo || !item.referencia.startsWith(PREFIJO_DE_TERRENO)) return [];

    const uuid = item.referencia.slice(PREFIJO_DE_TERRENO.length);

    return enlazadas.has(uuid) ? [] : [{ uuid, item }];
  });
}

/** "2 instalaciones y obras creadas en terreno": el título del aviso, concordado. */
export function tituloDelAviso(cuantas: number): string {
  return cuantas === 1
    ? "1 instalación u obra creada en terreno"
    : `${cuantas} instalaciones y obras creadas en terreno`;
}

/** La clave de la fila de una instalación de terreno (expansión del registro). */
export function claveDeTerreno(uuid: string): string {
  return `terreno-${uuid}`;
}

/** La clave de la fila de una instalación guardada. */
export function claveDeInstalacion(id: number): string {
  return `instalacion-${id}`;
}

/**
 * Un número de terreno como lo escribiría una persona. Un `0` se conserva: es
 * un dato; solo `null`/ausente queda vacío.
 */
function textoDelNumero(valor: number | null | undefined): string {
  return textoDeDecimal(valor == null ? null : String(valor));
}

/**
 * El formulario de alta precargado con lo que la App ya sabe: tipo, año,
 * cantidad y los dos valores unitarios, los que el encargado alcanzó a anotar.
 */
export function formularioDesdeElItem(item: ItemDeTerreno): FormularioDeInstalacion {
  return {
    ...INSTALACION_VACIA,
    tipo: item.nombre,
    anio: textoDeNumero(item.anio ?? null),
    cantidad: textoDelNumero(item.cantidad),
    valor_reposicion_uf_un: textoDelNumero(item.valor_reposicion_uf_un),
    valor_comercial_uf_un: textoDelNumero(item.valor_comercial_uf_un),
  };
}

/**
 * El ítem de terreno cuyos registros se muestran bajo una fila guardada: el
 * servidor ya colgó ahí lo enlazado (`instalacion:<id>`).
 */
export function itemDeLaInstalacion(
  items: ItemDeTerreno[],
  instalacion: Pick<InstalacionDeInforme, "id">,
): ItemDeTerreno | null {
  return items.find((i) => i.referencia === `instalacion:${instalacion.id}`) ?? null;
}

/**
 * Las filas con las que una instalación de terreno puede decir "es la misma
 * que": las ya guardadas que todavía no tienen un registro enlazado (el
 * servidor exige que cada uuid se use una sola vez por informe).
 */
export function opcionesParaVincular(
  instalaciones: Pick<InstalacionDeInforme, "id" | "tipo" | "terreno_ref">[],
): OpcionParaVincular[] {
  return instalaciones
    .filter((i) => i.terreno_ref === null)
    .map((i) => ({ clave: String(i.id), etiqueta: i.tipo }));
}

/** Lo que el encargado dijo de la descripción general de instalaciones y obras, o `null`. */
export function campoDeLaDescripcionDeInstalaciones(
  registro: RegistroDeTerrenoDelInforme | undefined,
): CampoDeTerreno | null {
  return registro?.secciones.find((s) => s.clave === "instalaciones")?.campos.find((c) => c.clave === "instalaciones") ?? null;
}
